/**
 * One automation: what it reads and what can be done to it, shared by the computer's page and the Season pages.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { AutomationDetail, AutomationPeople, AutomationStatus } from "../../shared/types";
import { type Editor, editorFromDetail } from "../components/FlowEditor";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { treeToGraph } from "./flow-model";
import { errorText } from "./words";

export function useAutomation(id: string) {
  const navigate = useNavigate();
  const toast = useToast();
  const [detail, setDetail] = useState<AutomationDetail | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [who, setWho] = useState<AutomationPeople | null>(null);
  const [missing, setMissing] = useState("");

  const take = useCallback((d: AutomationDetail) => {
    setDetail(d);
    setEditor(editorFromDetail(d));
  }, []);
  const reload = useCallback(() => {
    api.automations
      .get(id)
      .then(take)
      .catch(() => undefined);
  }, [id, take]);
  const readPeople = useCallback(() => {
    api.automations
      .people(id)
      .then(setWho)
      .catch(() => setWho(null));
  }, [id]);
  useEffect(() => {
    api.automations
      .get(id)
      .then(take)
      .catch((err) => setMissing(errorText(err, "That automation did not open")));
    readPeople();
  }, [id, take, readPeople]);

  const setStatus = (d: AutomationDetail, next: AutomationStatus) => {
    setDetail({ ...d, status: next });
    toast({ text: next === "on" ? `${d.name} is on.` : `${d.name} is off.`, icon: next === "on" ? "check" : "pause" });
    return api.automations
      .update(id, { status: next })
      .then((fresh) => setDetail(fresh))
      .catch((err) => {
        toast(errorText(err, "The switch did not save"));
        setDetail(d);
      });
  };

  const setRoom = (d: AutomationDetail, webinarId: string, on: boolean) => {
    const title = d.webinars.find((w) => w.webinar_id === webinarId)?.title ?? "this room";
    return api.automations
      .saveWebinars(id, { webinars: [{ webinar_id: webinarId, enabled: on }] })
      .then((fresh) => {
        setDetail(fresh);
        toast({ text: on ? `${d.name} is on for ${title}.` : `${d.name} is off for ${title}.`, icon: on ? "check" : "pause" });
      })
      .catch((err) => toast(errorText(err, "That room did not change")));
  };

  const rename = (d: AutomationDetail, ed: Editor, name: string) => {
    const clean = name.trim();
    if (!clean || clean === d.name) return Promise.resolve();
    setEditor({ ...ed, name: clean });
    return api.automations
      .update(id, { name: clean })
      .then((fresh) => setDetail(fresh))
      .catch((err) => toast(errorText(err, "The name did not save")));
  };

  /** Writes the flow as the editor holds it, then reads it back. Throws when the server refuses. */
  const saveFlow = async (ed: Editor, status: AutomationStatus) => {
    await api.automations.update(id, { name: ed.name, status, trigger: ed.trigger, stop_when: ed.stopWhen });
    await api.automations.saveGraph(id, { graph: treeToGraph(ed.steps) });
    take(await api.automations.get(id));
    readPeople();
  };

  /** Try again or Stop them for every run that stopped. */
  const settleRuns = async (d: AutomationDetail, how: "retry" | "stop") => {
    let last = d;
    for (const r of d.failed_runs) last = how === "retry" ? await api.automations.retryRun(d.id, r.id) : await api.automations.stopRun(d.id, r.id);
    setDetail(last);
    toast(how === "retry" ? "Trying them again now." : "Stopped. They leave this automation.");
  };

  const remove = (d: AutomationDetail) =>
    api.automations
      .remove(id)
      .then(() => {
        toast(`${d.name} deleted.`);
        navigate("/automations");
      })
      .catch((err) => toast(errorText(err, "The automation did not delete")));

  return { detail, setDetail, editor, setEditor, who, missing, take, reload, readPeople, setStatus, setRoom, rename, saveFlow, settleRuns, remove };
}
